import { useEffect, useRef, useState } from 'react';
import {
  createSpeechActivityMonitor,
  type SpeechActivityMonitor,
} from './speechActivityMonitor';
import {
  extensionForMimeType,
  isPermissionDeniedError,
  pickRecorderMimeType,
} from './speechRecorderMime';
import {
  formatSpeechRecordingElapsed,
  SPEECH_RECORDING_MAX_DURATION_MS,
} from './formatSpeechRecordingElapsed';

export { SPEECH_RECORDING_MAX_DURATION_MS, formatSpeechRecordingElapsed };

export type SpeechRecordingStatus =
  'idle' | 'requesting' | 'recording' | 'finalizing';

export type SpeechRecordingErrorReason =
  'permission-denied' | 'unavailable' | 'no-speech';

type UseSpeechRecordingOptions = {
  maxDurationMs?: number;
  onRecordingReady: (file: File) => void | Promise<void>;
  onError: (reason: SpeechRecordingErrorReason) => void;
};

export function useSpeechRecording({
  maxDurationMs = SPEECH_RECORDING_MAX_DURATION_MS,
  onRecordingReady,
  onError,
}: UseSpeechRecordingOptions) {
  const [status, setStatus] = useState<SpeechRecordingStatus>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const activityMonitorRef = useRef<SpeechActivityMonitor | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number | null>(null);
  const maxDurationTimeoutRef = useRef<number | null>(null);
  const elapsedIntervalRef = useRef<number | null>(null);
  const onRecordingReadyRef = useRef(onRecordingReady);
  const onErrorRef = useRef(onError);
  const stopRecordingRef = useRef<() => void>(() => {});
  /** Blocks overlapping startRecording calls (status alone is a render-stale race). */
  const startInFlightRef = useRef(false);
  /** Bumped on unmount / reset so late getUserMedia results are discarded. */
  const sessionGenerationRef = useRef(0);

  useEffect(() => {
    onRecordingReadyRef.current = onRecordingReady;
    onErrorRef.current = onError;
  }, [onRecordingReady, onError]);

  const clearTimers = () => {
    if (maxDurationTimeoutRef.current != null) {
      window.clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = null;
    }
    if (elapsedIntervalRef.current != null) {
      window.clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
  };

  const stopActivityMonitor = () => {
    activityMonitorRef.current?.stop();
    activityMonitorRef.current = null;
  };

  const stopMediaTracks = () => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  };

  const discardStream = (stream: MediaStream) => {
    stream.getTracks().forEach((track) => track.stop());
  };

  const resetIdle = () => {
    sessionGenerationRef.current += 1;
    startInFlightRef.current = false;
    clearTimers();
    stopActivityMonitor();
    stopMediaTracks();
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    startedAtRef.current = null;
    setElapsedMs(0);
    setStatus('idle');
  };

  useEffect(() => {
    return () => {
      sessionGenerationRef.current += 1;
      startInFlightRef.current = false;
      clearTimers();
      if (
        mediaRecorderRef.current != null &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop();
      }
      stopActivityMonitor();
      stopMediaTracks();
    };
  }, []);

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder == null || recorder.state === 'inactive') {
      return;
    }

    clearTimers();
    setStatus('finalizing');
    recorder.stop();
  };

  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  });

  const startRecording = async () => {
    if (startInFlightRef.current || status !== 'idle') {
      return;
    }

    if (
      typeof navigator === 'undefined' ||
      navigator.mediaDevices?.getUserMedia == null ||
      typeof MediaRecorder === 'undefined'
    ) {
      onErrorRef.current('unavailable');
      return;
    }

    startInFlightRef.current = true;
    const sessionGeneration = sessionGenerationRef.current;
    setStatus('requesting');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      if (sessionGeneration !== sessionGenerationRef.current) {
        discardStream(stream);
        return;
      }

      mediaStreamRef.current = stream;
      activityMonitorRef.current = createSpeechActivityMonitor(stream);

      const mimeType = pickRecorderMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      chunksRef.current = [];
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        resetIdle();
        onErrorRef.current('unavailable');
      };

      recorder.onstop = () => {
        clearTimers();
        const monitor = activityMonitorRef.current;
        const hadSpeech = monitor?.hasSpeech() ?? true;
        stopActivityMonitor();
        stopMediaTracks();

        const recordedMimeType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: recordedMimeType });
        chunksRef.current = [];
        mediaRecorderRef.current = null;
        startedAtRef.current = null;

        if (sessionGeneration !== sessionGenerationRef.current) {
          return;
        }

        if (!hadSpeech) {
          setElapsedMs(0);
          setStatus('idle');
          onErrorRef.current('no-speech');
          return;
        }

        const file = new File(
          [blob],
          `speech-recording.${extensionForMimeType(recordedMimeType)}`,
          { type: recordedMimeType },
        );

        void (async () => {
          try {
            await onRecordingReadyRef.current(file);
          } catch {
            // Caller is responsible for error UX (e.g. toast).
          } finally {
            if (sessionGeneration === sessionGenerationRef.current) {
              setElapsedMs(0);
              setStatus('idle');
            }
          }
        })();
      };

      recorder.onstart = () => {
        startInFlightRef.current = false;

        if (sessionGeneration !== sessionGenerationRef.current) {
          return;
        }

        startedAtRef.current = Date.now();
        setElapsedMs(0);
        setStatus('recording');

        elapsedIntervalRef.current = window.setInterval(() => {
          if (startedAtRef.current == null) {
            return;
          }
          setElapsedMs(Date.now() - startedAtRef.current);
        }, 200);

        maxDurationTimeoutRef.current = window.setTimeout(() => {
          stopRecordingRef.current();
        }, maxDurationMs);
      };

      recorder.start();
    } catch (error) {
      if (sessionGeneration !== sessionGenerationRef.current) {
        return;
      }

      resetIdle();
      onErrorRef.current(
        isPermissionDeniedError(error) ? 'permission-denied' : 'unavailable',
      );
    }
  };

  const toggleRecording = () => {
    if (status === 'recording' || status === 'finalizing') {
      stopRecording();
      return;
    }

    if (status === 'idle' && !startInFlightRef.current) {
      void startRecording();
    }
  };

  return {
    status,
    elapsedMs,
    isRecording: status === 'recording',
    isBusy: status === 'requesting' || status === 'finalizing',
    toggleRecording,
    stopRecording,
  };
}
